import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { homePathFor } from './lib/roles';
import { LoadingState } from './components/ui';
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { LoginPage } from './features/auth/LoginPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { MfaSetupPage } from './features/auth/MfaSetupPage';
import { OAuthCallback } from './features/auth/OAuthCallback';
import { DoctorDashboard } from './features/doctor/DoctorDashboard';
import { NewPrescription } from './features/doctor/NewPrescription';
import { PrescriptionDetail } from './features/doctor/PrescriptionDetail';
import { PrescriptionList } from './features/doctor/PrescriptionList';
import { PatientList } from './features/doctor/PatientList';
import { PatientDetail } from './features/doctor/PatientDetail';
import { NewPatient } from './features/doctor/NewPatient';
import { PharmacistDashboard } from './features/pharmacist/PharmacistDashboard';
import { QRScanner } from './features/pharmacist/QRScanner';
import { PrescriptionSearch } from './features/pharmacist/PrescriptionSearch';
import { PrescriptionView } from './features/pharmacist/PrescriptionView';
import { FulfillmentHistory } from './features/pharmacist/FulfillmentHistory';
import { PatientPortal } from './features/patient/PatientPortal';
import { AdminDashboard } from './features/admin/AdminDashboard';
import { UserManagement } from './features/admin/UserManagement';
import { DrugManagement } from './features/admin/DrugManagement';
import { AuditLogs } from './features/admin/AuditLogs';
import { GovernmentReports } from './features/admin/GovernmentReports';
import { SystemHealth } from './features/admin/SystemHealth';
import { DoctorReports } from './features/doctor/DoctorReports';
import { PharmacistReports } from './features/pharmacist/PharmacistReports';
import { SubscriptionPage } from './features/pharmacist/SubscriptionPage';

// Full page navigation (outside the SPA router and service worker), run as an effect
// rather than during render.
function HardRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <LoadingState />;
}

function SharedPrescriptionRedirect() {
  const { token } = useParams<{ token: string }>();
  return <HardRedirect to={`/_shared/index.html?token=${encodeURIComponent(token ?? '')}`} />;
}

function RootRedirect() {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <HardRedirect to="/landing.html" />;
  }

  return <Navigate to={homePathFor(user.role)} replace />;
}

function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: string[];
}) {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/oauth/callback" element={<OAuthCallback />} />
      </Route>

      {/* MFA enrollment requires a session, so it must not use the guest-only layout */}
      <Route
        element={
          <ProtectedRoute>
            <AuthLayout guestOnly={false} />
          </ProtectedRoute>
        }
      >
        <Route path="/mfa-setup" element={<MfaSetupPage />} />
      </Route>

      <Route
        path="/doctor"
        element={
          <ProtectedRoute allowedRoles={['DOCTOR']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DoctorDashboard />} />
        <Route path="prescriptions" element={<PrescriptionList />} />
        <Route path="prescriptions/new" element={<NewPrescription />} />
        <Route path="prescriptions/:id" element={<PrescriptionDetail />} />
        <Route path="patients" element={<PatientList />} />
        <Route path="patients/new" element={<NewPatient />} />
        <Route path="patients/:id" element={<PatientDetail />} />
        <Route path="reports" element={<DoctorReports />} />
      </Route>

      <Route
        path="/pharmacist"
        element={
          <ProtectedRoute allowedRoles={['PHARMACIST']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PharmacistDashboard />} />
        <Route path="scan" element={<QRScanner />} />
        <Route path="search" element={<PrescriptionSearch />} />
        <Route path="prescription/:id" element={<PrescriptionView />} />
        <Route path="history" element={<FulfillmentHistory />} />
        <Route path="reports" element={<PharmacistReports />} />
        <Route path="subscription" element={<SubscriptionPage />} />
      </Route>

      <Route
        path="/patient"
        element={
          <ProtectedRoute allowedRoles={['PATIENT']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PatientPortal />} />
      </Route>

      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AUDITOR']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="drugs" element={<DrugManagement />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="reports" element={<GovernmentReports />} />
        <Route path="health" element={<SystemHealth />} />
      </Route>

      <Route path="/p/:token" element={<SharedPrescriptionRedirect />} />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
